export const formatDateRange = (
  startDate: Date | null,
  endDate: Date | null,
  _timePeriod: 'period'
): string => {
  if (!startDate) return ''

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\./g, '.').replace(/ /g, '')
  }

  if (!endDate) {
    return formatDate(startDate)
  }

  return `${formatDate(startDate)} ~ ${formatDate(endDate)}`
}

export const getWeekRange = (date: Date): [Date, Date] => {
  const start = new Date(date)
  const day = start.getDay()
  const diff = start.getDate() - day + (day === 0 ? -6 : 1) // Monday as first day
  start.setDate(diff)

  const end = new Date(start)
  end.setDate(start.getDate() + 6)

  return [start, end]
}

export const getMonthRange = (date: Date): [Date, Date] => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1)
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)

  return [start, end]
}

export const adjustDateForPeriod = (
  date: Date,
  _timePeriod: 'period'
): [Date, Date] => {
  return [date, date]
}

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate()
}

export const isDateInRange = (date: Date, startDate: Date | null, endDate: Date | null): boolean => {
  if (!startDate || !endDate) return false
  return date >= startDate && date <= endDate
}

export const getDaysInMonth = (year: number, month: number): Date[] => {
  const date = new Date(year, month, 1)
  const days: Date[] = []

  while (date.getMonth() === month) {
    days.push(new Date(date))
    date.setDate(date.getDate() + 1)
  }

  return days
}

export const getCalendarDays = (year: number, month: number): Date[] => {
  const firstDay = new Date(year, month, 1)
  const startDate = new Date(firstDay)

  // Start from Monday of the week containing the first day
  const startDay = startDate.getDay()
  startDate.setDate(startDate.getDate() - (startDay === 0 ? 6 : startDay - 1))

  const days: Date[] = []
  const currentDate = new Date(startDate)

  // Always generate exactly 6 weeks (42 days) for consistent height
  for (let i = 0; i < 42; i++) {
    days.push(new Date(currentDate))
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return days
}

export const getWeekNumber = (date: Date): number => {
  // ISO week number calculation
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
}

export const getCalendarWeeks = (year: number, month: number): number[] => {
  const calendarDays = getCalendarDays(year, month)
  const weeks: number[] = []

  // Always generate exactly 6 weeks for consistent layout
  for (let i = 0; i < 6; i++) {
    const firstDayOfWeek = calendarDays[i * 7]
    weeks.push(getWeekNumber(firstDayOfWeek))
  }

  return weeks
}

export const getLastWeekRange = (): [Date, Date] => {
  const today = new Date()
  const currentDay = today.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate days to subtract to get to last Monday
  // If today is Sunday (0), we need to go back 6 days to get last Monday
  // If today is Monday (1), we need to go back 7 days to get last Monday
  // If today is Tuesday (2), we need to go back 8 days to get last Monday, etc.
  const daysToLastMonday = currentDay === 0 ? 6 : currentDay + 6

  const lastMonday = new Date(today)
  lastMonday.setDate(today.getDate() - daysToLastMonday)
  lastMonday.setHours(0, 0, 0, 0)

  const lastSunday = new Date(lastMonday)
  lastSunday.setDate(lastMonday.getDate() + 6)
  lastSunday.setHours(23, 59, 59, 999)

  return [lastMonday, lastSunday]
}

export const formatDateForAPI = (date: Date): string => {
  // Use local date formatting to avoid timezone issues
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// LocalStorage utilities for date range persistence
const STORAGE_KEY = 'dot_filter_period'

export interface DateRangePeriod {
  start_date: string
  end_date: string
}

export const getDefaultDateRange = (): DateRangePeriod => {
  const [lastMonday, lastSunday] = getLastWeekRange()
  return {
    start_date: formatDateForAPI(lastMonday),
    end_date: formatDateForAPI(lastSunday)
  }
}

export const loadDateRangeFromStorage = (): DateRangePeriod => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as DateRangePeriod
      // Validate that the stored data has the required structure
      if (parsed.start_date && parsed.end_date) {
        return parsed
      }
    }
  } catch (error) {
    console.warn('Failed to load date range from localStorage:', error)
  }

  // Return default if no valid data found
  return getDefaultDateRange()
}

export const saveDateRangeToStorage = (startDate: Date, endDate: Date): void => {
  try {
    const dateRange: DateRangePeriod = {
      start_date: formatDateForAPI(startDate),
      end_date: formatDateForAPI(endDate)
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dateRange))
  } catch (error) {
    console.warn('Failed to save date range to localStorage:', error)
  }
}

export const parseDateFromStorage = (dateString: string): Date => {
  return new Date(dateString + 'T00:00:00')
}

export const getInitialDateRange = (): [Date, Date] => {
  const stored = loadDateRangeFromStorage()
  return [
    parseDateFromStorage(stored.start_date),
    parseDateFromStorage(stored.end_date)
  ]
}